import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { type CartItem, formatCartQuantity, getCartItemQuantityContext } from "@ecommerce/store";
import {
    numberFormatter,
    productQuantity,
    type ProductQuantityValidationError,
} from "@ecommerce/shared-utils";
import { useSnackbar } from "notistack";
import { STYLES } from "./ShoppingCartStyles";
import { Icon } from "../liquid/LiquidPrimitives";

interface ShoppingCartItemProps {
    item: CartItem;
    onUpdateQuantity: (
        idProduct: string,
        idProductVariant: string,
        quantity: number,
    ) => Promise<void>;
    onRemoveItem: (idProduct: string, idProductVariant: string) => void;
}

export const ShoppingCartItem: React.FC<ShoppingCartItemProps> = ({
    item,
    onUpdateQuantity,
    onRemoveItem,
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const quantityContext = getCartItemQuantityContext(item);
    const { cantDecimal, minimumQuantity, maximumQuantity, stock, unitLabel, rules } =
        quantityContext;
    const isPurchasable = maximumQuantity >= minimumQuantity;
    const formatQuantity = (value: number) => formatCartQuantity(item, value);
    const [inputValue, setInputValue] = useState(() => formatQuantity(item.quantity));
    const [isUpdating, setIsUpdating] = useState(false);

    useEffect(() => {
        setInputValue(formatQuantity(item.quantity));
    }, [cantDecimal, item.quantity]);

    const updateQuantity = async (quantity: number) => {
        if (isUpdating) return;

        const normalizedQuantity = productQuantity.normalize(quantity, cantDecimal);
        const validation = productQuantity.validate(normalizedQuantity, rules);
        if (!validation.valid) {
            showValidationError(validation.error ?? "parse", formatQuantity(normalizedQuantity));
            setInputValue(formatQuantity(item.quantity));
            return;
        }

        setInputValue(formatQuantity(normalizedQuantity));
        setIsUpdating(true);
        try {
            await onUpdateQuantity(item.idProduct, item.idProductVariant, normalizedQuantity);
        } catch {
            setInputValue(formatQuantity(item.quantity));
            enqueueSnackbar("No pudimos actualizar la cantidad", {
                variant: "error",
            });
        } finally {
            setIsUpdating(false);
        }
    };

    const showValidationError = (
        error: ProductQuantityValidationError | "parse",
        rawValue: string,
    ) => {
        const numericValue = Number(rawValue.trim().replace(",", "."));
        let message = "Ingresá una cantidad válida";

        if (Number.isFinite(numericValue) && numericValue <= 0) {
            message = "La cantidad debe ser mayor a cero";
        } else if (error === "invalid-precision" || (error === "parse" && cantDecimal === 0)) {
            message = "Este producto sólo admite cantidades enteras";
        } else if (error === "below-minimum") {
            message = `La cantidad mínima es ${formatQuantity(minimumQuantity)} ${unitLabel}`;
        } else if (error === "above-stock") {
            message = `La cantidad máxima es ${formatQuantity(maximumQuantity)} ${unitLabel}`;
        }

        enqueueSnackbar(message, { variant: "warning" });
    };

    const resolveInputQuantity = (): number | null => {
        const parsedQuantity = productQuantity.parseInput(inputValue, cantDecimal);
        if (parsedQuantity == null) {
            showValidationError("parse", inputValue);
            setInputValue(formatQuantity(item.quantity));
            return null;
        }

        const normalizedQuantity = productQuantity.normalize(parsedQuantity, cantDecimal);
        const validation = productQuantity.validate(normalizedQuantity, rules);
        if (!validation.valid) {
            showValidationError(validation.error ?? "parse", inputValue);
            setInputValue(formatQuantity(item.quantity));
            return null;
        }

        return normalizedQuantity;
    };

    const confirmInput = () => {
        const quantity = resolveInputQuantity();
        if (quantity != null) void updateQuantity(quantity);
    };

    const handleIncrement = () => {
        if (!isPurchasable || isUpdating) return;
        const quantity = resolveInputQuantity();
        if (quantity == null) return;

        const incremented = productQuantity.increment(quantity, rules);
        if (incremented === quantity && quantity >= maximumQuantity) {
            showValidationError("above-stock", formatQuantity(quantity));
            setInputValue(formatQuantity(item.quantity));
            return;
        }

        void updateQuantity(incremented);
    };

    const handleDecrement = () => {
        if (isUpdating) return;
        const quantity = resolveInputQuantity();
        if (quantity != null) {
            void updateQuantity(productQuantity.decrement(quantity, rules));
        }
    };

    const canDecrement = !isUpdating && item.quantity > minimumQuantity;
    const canIncrement = !isUpdating && isPurchasable && item.quantity < maximumQuantity;
    const baseMinimum = productQuantity.getMinimum({ cantDecimal });

    const btnStyle: React.CSSProperties = {
        width: 28,
        height: 28,
        borderRadius: 999,
        border: "none",
        background: "white",
        boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    };

    return (
        <Box
            sx={{
                mb: 2.5,
                pb: 2.5,
                borderBottom: "1px solid var(--divider)",
                "&:last-child": { borderBottom: "none", mb: 0, pb: 0 },
            }}
        >
            <Box sx={{ display: "flex", gap: 1.5 }}>
                <Box
                    component="img"
                    src={item.image}
                    alt={item.name}
                    sx={{
                        width: 72,
                        height: 96,
                        objectFit: "cover",
                        borderRadius: "10px",
                        flexShrink: 0,
                    }}
                />

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontFamily: STYLES.fontFamily,
                            fontSize: "12px",
                            fontWeight: 600,
                            color: "#1a1c1e",
                            textTransform: "uppercase",
                            letterSpacing: "0.03em",
                            lineHeight: 1.3,
                            mb: 0.5,
                            pr: 3,
                        }}
                    >
                        {item.name}
                    </Typography>

                    {item.attributes.length > 0 && (
                        <Typography
                            sx={{
                                fontFamily: STYLES.fontFamily,
                                fontSize: "11px",
                                color: "#707882",
                                mb: 0.75,
                                textTransform: "capitalize",
                            }}
                        >
                            {item.attributes.map((attribute) => attribute.value).join(", ")}
                        </Typography>
                    )}

                    <Typography
                        sx={{
                            fontFamily: "'SF Mono', 'Fira Code', monospace",
                            fontSize: "14px",
                            fontWeight: 700,
                            color: "#1a1c1e",
                            letterSpacing: "-0.02em",
                            mb: 1,
                        }}
                    >
                        {numberFormatter.toStringWithAmount(item.price, "$")}{" "}
                        <span style={{ fontWeight: 400, fontSize: 11, color: "#707882" }}>
                            / mes
                        </span>
                    </Typography>

                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            padding: "3px",
                            borderRadius: 999,
                            background: "rgba(10,10,10,0.06)",
                            width: "fit-content",
                        }}
                    >
                        <button
                            type="button"
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={handleDecrement}
                            disabled={!canDecrement}
                            style={{
                                ...btnStyle,
                                cursor: canDecrement ? "pointer" : "not-allowed",
                                opacity: canDecrement ? 1 : 0.4,
                            }}
                            aria-label="Restar cantidad"
                        >
                            <Icon.minus />
                        </button>
                        <input
                            type="text"
                            inputMode={cantDecimal === 0 ? "numeric" : "decimal"}
                            step={productQuantity.getInputStep(cantDecimal)}
                            min={minimumQuantity}
                            max={maximumQuantity}
                            disabled={isUpdating}
                            aria-label="Cantidad"
                            value={inputValue}
                            onChange={(event) => setInputValue(event.target.value)}
                            onBlur={confirmInput}
                            onKeyDown={(event) => {
                                if (event.key === "Enter") {
                                    event.preventDefault();
                                    event.currentTarget.blur();
                                } else if (event.key === "Escape") {
                                    event.preventDefault();
                                    setInputValue(formatQuantity(item.quantity));
                                }
                            }}
                            style={{
                                fontFamily: "ui-monospace, 'SF Mono', monospace",
                                fontSize: 13,
                                fontWeight: 600,
                                width: cantDecimal === 0 ? 30 : 48,
                                textAlign: "center",
                                color: "#1a1c1e",
                                border: "none",
                                outline: "none",
                                background: "transparent",
                            }}
                        />
                        <button
                            type="button"
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={handleIncrement}
                            disabled={!canIncrement}
                            style={{
                                ...btnStyle,
                                cursor: canIncrement ? "pointer" : "not-allowed",
                                opacity: canIncrement ? 1 : 0.4,
                            }}
                            aria-label="Sumar cantidad"
                        >
                            <Icon.plus />
                        </button>
                    </div>

                    <Typography
                        sx={{
                            fontFamily: STYLES.fontFamily,
                            fontSize: "10px",
                            color: "#707882",
                            mt: 0.75,
                        }}
                    >
                        {stock === undefined
                            ? `Disponible · ${unitLabel}`
                            : `Stock ${formatQuantity(stock)} ${unitLabel}`}
                        {minimumQuantity > baseMinimum &&
                            ` · Mínimo ${formatQuantity(minimumQuantity)} ${unitLabel}`}
                    </Typography>

                    <Typography
                        sx={{
                            fontFamily: "'SF Mono', 'Fira Code', monospace",
                            fontSize: "11px",
                            fontWeight: 600,
                            color: "#1a1c1e",
                            mt: 0.5,
                        }}
                    >
                        Subtotal{" "}
                        {numberFormatter.toStringWithAmount(item.price * item.quantity, "$")}
                    </Typography>
                </Box>

                <button
                    type="button"
                    onClick={() => onRemoveItem(item.idProduct, item.idProductVariant)}
                    style={{
                        width: 28,
                        height: 28,
                        borderRadius: 999,
                        border: "none",
                        background: "rgba(26,28,30,0.07)",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        alignSelf: "flex-start",
                        color: "#707882",
                    }}
                    aria-label="Eliminar producto"
                >
                    <Icon.trash />
                </button>
            </Box>
        </Box>
    );
};
